package com.milta.feature.route.data.local

import com.milta.core.cache.LocalCacheStore
import com.milta.feature.route.domain.entity.DeliveryOutcome
import com.milta.feature.route.domain.entity.PendingDeliveryValidation
import com.milta.feature.route.domain.entity.PendingSyncState
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

private const val QUEUE_KEY = "pending_deliveries"
private const val PHOTO_KEY_PREFIX = "pending_photo:"

/**
 * Persisted store for the offline validation queue (mode hors ligne, au-delà de MIL-43) — backed
 * by [LocalCacheStore] so the queue survives the app process being killed while offline (previously
 * in-memory only, an acknowledged gap the MIL-43 mockup itself documented, notes point 5).
 *
 * Raw [ByteArray] photo payloads are Base64-encoded and stored one [LocalCacheStore] entry per item
 * (keyed by [PendingDeliveryValidation.id]), kept out of the queue's own JSON blob and out of the
 * observable [items] `StateFlow` — same precedent as `DeliveryProofViewModel` keeping `photoBytes`
 * out of its observable state. A real filesystem (cacheDir-style) would avoid the ~33% Base64
 * overhead, but there is no existing multiplatform file-write abstraction in this codebase to reuse
 * (the OCR/import flows that write to `cacheDir/camera/` are Android-only, in `androidApp/`) —
 * reusing [LocalCacheStore] avoids introducing a second, iOS/Wasm-incompatible storage primitive
 * for what is, in practice, a handful of queued items at a time.
 */
@OptIn(ExperimentalEncodingApi::class)
class PendingDeliveryValidationLocalDataSource(private val cacheStore: LocalCacheStore) {

    private val _items = MutableStateFlow(loadPersistedItems())
    val items: StateFlow<List<PendingDeliveryValidation>> = _items

    @OptIn(ExperimentalUuidApi::class)
    fun add(
        routeId: String,
        deliveryId: String,
        recipientName: String,
        address: String,
        status: DeliveryOutcome,
        comment: String?,
        photoBytes: ByteArray?,
        photoFileName: String?,
        occurredAt: String,
        capturedAtLabel: String,
    ): PendingDeliveryValidation {
        val item = PendingDeliveryValidation(
            id = Uuid.random().toString(),
            routeId = routeId,
            deliveryId = deliveryId,
            recipientName = recipientName,
            address = address,
            status = status,
            comment = comment,
            photoFileName = photoFileName.takeIf { photoBytes != null },
            occurredAt = occurredAt,
            capturedAtLabel = capturedAtLabel,
        )
        // Retour 27/10 (point 3) — a validation without photo stores no payload.
        if (photoBytes != null) cacheStore.putString(PHOTO_KEY_PREFIX + item.id, Base64.encode(photoBytes))
        _items.update { (it + item).also(::persist) }
        return item
    }

    fun photoBytesFor(id: String): ByteArray? =
        cacheStore.getString(PHOTO_KEY_PREFIX + id)?.let { Base64.decode(it) }

    fun updateSyncState(id: String, syncState: PendingSyncState, lastErrorCode: String? = null) {
        _items.update { list ->
            list.map { if (it.id == id) it.copy(syncState = syncState, lastErrorCode = lastErrorCode) else it }
                .also(::persist)
        }
    }

    fun remove(id: String) {
        cacheStore.remove(PHOTO_KEY_PREFIX + id)
        _items.update { list -> list.filterNot { it.id == id }.also(::persist) }
    }

    private fun persist(list: List<PendingDeliveryValidation>) {
        cacheStore.putString(QUEUE_KEY, Json.encodeToString(list))
    }

    private fun loadPersistedItems(): List<PendingDeliveryValidation> {
        val raw = cacheStore.getString(QUEUE_KEY) ?: return emptyList()
        return runCatching { Json.decodeFromString<List<PendingDeliveryValidation>>(raw) }.getOrDefault(emptyList())
    }
}
